import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { sendEarlyAccessNotification, sendContactNotification } from './emailNotifications';

// Generate unique document ID with timestamp and microseconds to avoid conflicts
export const generateDocId = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const date = now.getDate().toString().padStart(2, '0');
  const timestamp = now.getTime(); // milliseconds since epoch
  const microseconds = performance.now().toString().replace('.', '').slice(-6); // add microseconds for uniqueness
  
  return `${year}${month}${date}_${timestamp}_${microseconds}`;
};

// Interface for early access form data
export interface EarlyAccessFormData {
  name: string;
  email: string;
  company: string;
  useCase: string;
  language?: string; // detected user language
  userAgent?: string; // browser info
  referrer?: string; // referrer URL
}

// Interface for contact form data  
export interface ContactFormData {
  name: string;
  email: string;
  company: string;
  useCase: string;
  language?: string; // detected user language
  userAgent?: string; // browser info
  referrer?: string; // referrer URL
}

// Submit early access form
export const submitEarlyAccessForm = async (formData: EarlyAccessFormData): Promise<string> => {
  try {
    // Add metadata
    const submissionData = {
      ...formData,
      submittedAt: serverTimestamp(),
      language: formData.language || navigator.language || 'en',
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      url: window.location.href,
      type: 'earlyAccess'
    };

    // Generate custom document ID
    const docId = generateDocId();
    
    // Create document reference with custom ID
    const docRef = doc(db, 'earlyAccess', docId);
    
    // Set document data with custom ID as the document ID
    await setDoc(docRef, {
      ...submissionData,
      documentId: docId // store the ID in the document too for reference
    });
    
    console.log('Early access form submitted with ID: ', docId);
    
    // Send notification email (don't await to avoid blocking user experience)
    sendEarlyAccessNotification(submissionData).catch(error => {
      console.error('Failed to send early access notification email:', error);
      // Don't throw error - we don't want email failures to break form submission
    });
    
    return docId;
  } catch (error) {
    console.error('Error submitting early access form: ', error);
    throw new Error('Failed to submit form. Please try again.');
  }
};

// Submit contact form
export const submitContactForm = async (formData: ContactFormData): Promise<string> => {
  try {
    // Add metadata
    const submissionData = {
      ...formData,
      submittedAt: serverTimestamp(),
      language: formData.language || navigator.language || 'en',
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      url: window.location.href,
      type: 'contact'
    };

    // Generate custom document ID
    const docId = generateDocId();
    
    // Create document reference with custom ID
    const docRef = doc(db, 'contacts', docId);
    
    // Set document data with custom ID as the document ID
    await setDoc(docRef, {
      ...submissionData,
      documentId: docId // store the ID in the document too for reference
    });
    
    console.log('Contact form submitted with ID: ', docId);
    
    // Send notification email (don't await to avoid blocking user experience)
    sendContactNotification(submissionData).catch(error => {
      console.error('Failed to send contact notification email:', error);
      // Don't throw error - we don't want email failures to break form submission
    });
    
    return docId;
  } catch (error) {
    console.error('Error submitting contact form: ', error);
    throw new Error('Failed to submit form. Please try again.');
  }
};